<template>
  <BasePopup
    v-model="isOpen"
    max-width="566px"
    min-height="658px"
    max-height="658px"
    :with-close="!props.persistent"
    :persistent="props.persistent"
    full-height-on-mobile
  >
    <BaseIsland
      size="xxl"
      class="auth-popup__content"
      :with-shadow="false"
    >
      <!-- Welcome -->
      <template v-if="currentStep === 'welcome'">
        <AuthHeader
          :title="t('Добро пожаловать!')"
          :subtitle="t('Войдите в свой аккаунт или зарегистрируйтесь, чтобы получить полный доступ.')"
        />
        <div class="auth-popup__actions">
          <BaseButton
            variant="dark"
            with-full
            with-decor
            data-testid="open-signin-btn"
            @click="goToStep('signin')"
          >
            {{ t('Войти') }}
          </BaseButton>
          <BaseButton
            variant="primary"
            with-full
            with-decor
            data-testid="open-signup-btn"
            @click="goToStep('signup-method')"
          >
            {{ t('Зарегистрироваться') }}
          </BaseButton>
        </div>
        <AuthContactDevelopersBlock />
      </template>

      <!-- Signin -->
      <template v-else-if="currentStep === 'signin'">
        <AuthHeader
          :title="t('Авторизация')"
          :subtitle="t('Войдите в свой аккаунт или зарегистрируйтесь, чтобы получить полный доступ.')"
        />
        <AuthSigninForm
          :show-back-button="true"
          :on-forgot-password="() => goToStep('forgot')"
          @success="handleSigninSuccess"
          @back="goToStep('welcome')"
        />
      </template>

      <!-- Signup method selection -->
      <template v-else-if="currentStep === 'signup-method'">
        <AuthHeader
          :title="t('Регистрация')"
          :subtitle="t('Войдите в свой аккаунт или зарегистрируйтесь, чтобы получить полный доступ.')"
        />
        <div class="auth-popup__actions">
          <BaseButton
            variant="dark"
            with-full
            with-decor
            data-testid="auth-method-phone-btn"
            @click="goToStep('signup-form', { method: AuthMethod.phone })"
          >
            {{ t('По телефону') }}
            <template #append-icon>
              <BaseIcon
                name="vuesax:mobile"
                color="var(--color-foreground-light)"
                size="20px"
              />
            </template>
          </BaseButton>
          <BaseButton
            variant="primary"
            with-full
            with-decor
            data-testid="auth-method-email-btn"
            @click="goToStep('signup-form', { method: AuthMethod.email })"
          >
            {{ t('По почте') }}
            <template #append-icon>
              <BaseIcon
                name="vuesax:sms"
                color="var(--color-foreground-light)"
                size="20px"
              />
            </template>
          </BaseButton>
          <NuxtLink
            class="auth-popup__link"
            @click.prevent="goToStep('signin')"
          >
            {{ t('Уже есть аккаунт? Войти') }}
          </NuxtLink>
        </div>
        <AuthContactDevelopersBlock />
      </template>

      <!-- Signup form -->
      <template v-else-if="currentStep === 'signup-form'">
        <AuthHeader
          :title="t('Регистрация')"
          :subtitle="t('Войдите в свой аккаунт или зарегистрируйтесь, чтобы получить полный доступ.')"
        />
        <AuthSignupForm
          :method="(stepParams.method ?? AuthMethod.phone) as AuthMethod"
          @success="goToStep('verify', { method: stepParams.method })"
          @back="goToStep('signup-method')"
        />
      </template>

      <!-- Forgot password -->
      <template v-else-if="currentStep === 'forgot'">
        <AuthHeader
          :title="t('Восстановление пароля')"
          :subtitle="t('Войдите в свой аккаунт или зарегистрируйтесь, чтобы получить полный доступ.')"
        />
        <AuthForgotForm
          @success="goToStep('verify', { method: $event, isReset: true })"
          @back="goToStep('signin')"
        />
      </template>

      <!-- Verify -->
      <template v-else-if="currentStep === 'verify'">
        <AuthVerifyForm
          :is-reset="stepParams.isReset ?? false"
          :method="stepParams.method ?? AuthMethod.phone"
          @success="handleVerifySuccess"
          @back="handleVerifyBack"
        />
      </template>

      <!-- Profile -->
      <template v-else-if="currentStep === 'profile'">
        <AuthProfileForm
          :method="stepParams.method ?? AuthMethod.phone"
          @success="goToStep('set-password', { method: stepParams.method })"
          @back="goToStep('verify', { method: stepParams.method })"
        />
      </template>

      <!-- Set password -->
      <template v-else-if="currentStep === 'set-password'">
        <AuthSetPasswordForm
          :is-reset="stepParams.isReset ?? false"
          :method="stepParams.method ?? AuthMethod.phone"
          @success="(isReset: boolean) => handleSetPasswordSuccess(isReset)"
          @back="handleSetPasswordBack"
        />
      </template>

      <!-- Success -->
      <template v-else-if="currentStep === 'success'">
        <AuthHeader
          :title="t('Ваш пароль успешно изменён')"
        />
        <BaseButton
          variant="primary"
          with-decor
          with-full
          @click="goToStep('signin')"
        >
          {{ t('Войти') }}
        </BaseButton>
        <AuthContactDevelopersBlock />
      </template>
    </BaseIsland>
  </BasePopup>
</template>

<script setup lang="ts">
import type { AuthPopupStep, AuthPopupStepParams } from '../model/steps'

import { useI18n } from 'vue-i18n'

import { AuthContactDevelopersBlock, AuthHeader, AuthMethod } from '~/entities/auth'
import { BaseButton, BaseIcon, BaseIsland, BasePopup } from '~/shared/ui'
import { AuthForgotForm } from '../../auth-forgot-form'
import { AuthProfileForm } from '../../auth-profile-form'
import { AuthSetPasswordForm } from '../../auth-set-password-form'
import { AuthSigninForm } from '../../auth-signin-form'
import { AuthSignupForm } from '../../auth-signup-form'
import { AuthVerifyForm } from '../../auth-verify-form'
import i18n from '../model/AuthPopup.i18n.json'

const props = withDefaults(defineProps<Props>(), {
  persistent: false,
})
const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void
  (e: 'success'): void
}>()

interface Props {
  modelValue: boolean
  persistent?: boolean
}

const { t } = useI18n(i18n)
const router = useRouter()
const route = useRoute()

const isOpen = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})

const currentStep = ref<AuthPopupStep>('welcome')
const stepHistory = ref<Array<{ step: AuthPopupStep, params: AuthPopupStepParams }>>([])
const stepParams = ref<AuthPopupStepParams>({})

watch(isOpen, (open) => {
  if (open) {
    currentStep.value = 'welcome'
    stepHistory.value = []
    stepParams.value = {}
  }
})

function goToStep(step: AuthPopupStep, params: AuthPopupStepParams = {}) {
  stepHistory.value.push({ step: currentStep.value, params: { ...stepParams.value } })
  currentStep.value = step
  stepParams.value = params
  syncRouteQuery(params)
}

function syncRouteQuery(params: AuthPopupStepParams) {
  const query: Record<string, string> = { ...route.query as Record<string, string> }
  if (params.method) {
    query.method = params.method
  }
  else if ('method' in query) {
    delete query.method
  }
  if (params.isReset !== undefined) {
    query.isReset = String(params.isReset)
  }
  router.replace({ query })
}

function goBack() {
  const prev = stepHistory.value.pop()
  if (prev) {
    currentStep.value = prev.step
    stepParams.value = prev.params
    syncRouteQuery(prev.params)
  }
}

function handleSigninSuccess() {
  emit('success')
  if (props.persistent) {
    window.location.reload()
  }
  else {
    isOpen.value = false
  }
}

function handleVerifySuccess(params: { nextStep: 'profile' | 'set-password', method?: AuthMethod, isReset?: boolean }) {
  goToStep(params.nextStep, {
    method: params.method ?? stepParams.value.method,
    isReset: params.isReset ?? stepParams.value.isReset,
  })
}

function handleVerifyBack() {
  goBack()
}

function handleSetPasswordSuccess(isReset: boolean) {
  if (isReset) {
    goToStep('success')
    return
  }
  emit('success')
  if (props.persistent) {
    window.location.reload()
  }
  else {
    isOpen.value = false
  }
}

function handleSetPasswordBack() {
  goBack()
}
</script>

<style lang="scss" scoped>
.auth-popup {
  &__content {
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    gap: rem(24px);
  }

  &__actions {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: rem(12px);
  }

  &__link {
    font-weight: 700;
    color: var(--color-foreground-primary);
    cursor: pointer;
  }
}
</style>
